/**
 * Pilotage-plan building toolbar — a floating, collapsible bar over the chart
 * (chart stays visible behind it) shown whenever a plan is being built. There are
 * no modes: this toolbar alone drives plan-building. Pick a tool, then tap the
 * chart; the depth-contour tool opens a small Follow / Clearing-depth chooser, and
 * a sticky note opens a text editor here. Collapses to a handle for maximum chart.
 *
 * It reads and writes the planner store only; the chart taps themselves are
 * handled in the map interaction module.
 */
import { useState } from 'react';
import { usePilotagePlannerStore, selectActivePlan } from '@/store/pilotagePlanner';
import type { PlanTool } from '@/store/pilotagePlanner';
import { haptic } from '@/ui/haptics';

const TOOLS: { id: PlanTool; icon: string; label: string; hint: string }[] = [
  { id: 'route', icon: '›', label: 'Route', hint: 'Tap to lay waypoints leg by leg. Drag a waypoint to move it.' },
  { id: 'wheelover', icon: '↻', label: 'Wheel-over', hint: 'Tap a waypoint, then the landmark whose bearing triggers the turn.' },
  { id: 'transit', icon: '⋯', label: 'Transit', hint: 'Tap the rear object, then the front object, to set a transit.' },
  { id: 'clearing', icon: '∟', label: 'Clearing', hint: 'Tap the charted object, then a point on the safe side.' },
  { id: 'back', icon: '↩', label: 'Back brg', hint: 'Tap the object, then the vessel side, for a back bearing.' },
  { id: 'contour', icon: '≈', label: 'Contour', hint: 'Tap a depth contour to follow it or set it as a clearing depth.' },
  { id: 'freehand', icon: '✎', label: 'Freehand', hint: 'Press and drag to sketch a line or arrow on the chart.' },
  { id: 'note', icon: '≡', label: 'Note', hint: 'Tap to drop a sticky note, then type what it should say.' },
];

export function PilotagePlanToolbar() {
  const building = usePilotagePlannerStore((s) => s.building);
  const plans = usePilotagePlannerStore((s) => s.plans);
  const activePlanId = usePilotagePlannerStore((s) => s.activePlanId);
  const activeTool = usePilotagePlannerStore((s) => s.activeTool);
  const setTool = usePilotagePlannerStore((s) => s.setTool);
  const setBuilding = usePilotagePlannerStore((s) => s.setBuilding);
  const planVisible = usePilotagePlannerStore((s) => s.planVisible);
  const setPlanVisible = usePilotagePlannerStore((s) => s.setPlanVisible);
  const setBaseSpeed = usePilotagePlannerStore((s) => s.setBaseSpeed);
  const renamePlan = usePilotagePlannerStore((s) => s.renamePlan);

  const pendingContour = usePilotagePlannerStore((s) => s.pendingContour);
  const setPendingContour = usePilotagePlannerStore((s) => s.setPendingContour);
  const addClearingDepth = usePilotagePlannerStore((s) => s.addClearingDepth);
  const openNoteId = usePilotagePlannerStore((s) => s.openNoteId);
  const setOpenNote = usePilotagePlannerStore((s) => s.setOpenNote);
  const updateStickyNote = usePilotagePlannerStore((s) => s.updateStickyNote);
  const removeStickyNote = usePilotagePlannerStore((s) => s.removeStickyNote);

  const [collapsed, setCollapsed] = useState(false);
  const [shallowSide, setShallowSide] = useState<'left' | 'right'>('right');
  const [renaming, setRenaming] = useState(false);
  const [nameText, setNameText] = useState('');

  const plan = selectActivePlan({ plans, activePlanId });
  if (!building || !plan) return null;

  const note = openNoteId ? plan.stickyNotes.find((n) => n.id === openNoteId) : undefined;
  const activeHint = TOOLS.find((t) => t.id === activeTool)?.hint;

  if (collapsed) {
    return (
      <button
        type="button"
        className="acp-plan-toolbar-handle"
        onClick={() => setCollapsed(false)}
        aria-label="Show plan toolbar"
      >
        🛠 Plan
      </button>
    );
  }

  return (
    <div className="acp-plan-toolbar" role="toolbar" aria-label="Pilotage plan tools">
      <div className="acp-plan-toolbar-head">
        {renaming ? (
          <input
            className="acp-text-input"
            style={{ flex: 1, minWidth: 0 }}
            value={nameText}
            autoFocus
            aria-label="Plan name"
            onChange={(e) => setNameText(e.target.value)}
            onBlur={() => {
              if (nameText.trim()) renamePlan(plan.id, nameText.trim());
              setRenaming(false);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                if (nameText.trim()) renamePlan(plan.id, nameText.trim());
                setRenaming(false);
              }
            }}
          />
        ) : (
          <button
            type="button"
            className="acp-plan-name"
            onClick={() => {
              setNameText(plan.name);
              setRenaming(true);
            }}
            title="Rename plan"
          >
            {plan.name}
          </button>
        )}
        <button type="button" className="acp-btn" onClick={() => setPlanVisible(!planVisible)} aria-pressed={planVisible} title="Show or hide the plan">
          {planVisible ? '👁' : '🚫'}
        </button>
        <button type="button" className="acp-btn" onClick={() => setCollapsed(true)} aria-label="Collapse toolbar">
          ▾
        </button>
        <button
          type="button"
          className="acp-btn primary"
          onClick={() => {
            setTool(null);
            setBuilding(false);
          }}
        >
          Done
        </button>
      </div>

      <div className="acp-plan-tools">
        {TOOLS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`acp-plan-tool${activeTool === t.id ? ' on' : ''}`}
            onClick={() => {
              haptic('tick');
              // Set the tool (don't toggle off on re-tap) so a second tap on the
              // armed tool can't silently disarm plotting.
              setTool(t.id);
            }}
          >
            <span className="ic" aria-hidden="true">{t.icon}</span>
            <span className="lb">{t.label}</span>
          </button>
        ))}
      </div>

      {/* Context panel: contour chooser, note editor, or the active tool's hint. */}
      {pendingContour && !pendingContour.following ? (
        <div className="acp-plan-context">
          <b>Depth contour{pendingContour.depthM != null ? ` (${pendingContour.depthM} m)` : ''}</b>
          <div className="acp-btn-row">
            <button
              type="button"
              className="acp-btn"
              onClick={() => setPendingContour({ ...pendingContour, following: true })}
            >
              Follow as route
            </button>
          </div>
          <div className="acp-plan-side">
            <span>Shallow side:</span>
            <button type="button" className={`acp-btn${shallowSide === 'left' ? ' primary' : ''}`} onClick={() => setShallowSide('left')}>Left</button>
            <button type="button" className={`acp-btn${shallowSide === 'right' ? ' primary' : ''}`} onClick={() => setShallowSide('right')}>Right</button>
            <button
              type="button"
              className="acp-btn"
              onClick={() => {
                addClearingDepth(pendingContour.path, shallowSide, pendingContour.depthM);
                setPendingContour(null);
              }}
            >
              Set as clearing depth
            </button>
          </div>
          <button type="button" className="acp-btn" onClick={() => setPendingContour(null)}>Cancel</button>
        </div>
      ) : note ? (
        <div className="acp-plan-context">
          <b>Sticky note</b>
          <textarea
            className="acp-text-input"
            rows={3}
            style={{ width: '100%', resize: 'vertical' }}
            placeholder="e.g. Marina VHF 80 · lock opens HW−2 · call the keeper"
            value={note.text}
            autoFocus
            onChange={(e) => updateStickyNote(note.id, e.target.value)}
          />
          <div className="acp-btn-row">
            <button type="button" className="acp-btn" onClick={() => { removeStickyNote(note.id); setOpenNote(null); }}>Delete</button>
            <button type="button" className="acp-btn primary" onClick={() => setOpenNote(null)}>Done</button>
          </div>
        </div>
      ) : (
        <div className="acp-plan-context">
          {activeTool ? (
            <span className="acp-plan-hint">{activeHint}</span>
          ) : (
            <span className="acp-plan-hint">Pick a tool to start. Tap a waypoint or note to select it.</span>
          )}
          <span className="acp-plan-hint" style={{ opacity: 0.8 }}>
            {plan.route.waypoints.length} waypoint{plan.route.waypoints.length === 1 ? '' : 's'} ·{' '}
            {plan.transits.length + plan.clearingBearings.length + plan.backBearings.length} bearing
            {plan.transits.length + plan.clearingBearings.length + plan.backBearings.length === 1 ? '' : 's'} ·{' '}
            {plan.stickyNotes.length} note{plan.stickyNotes.length === 1 ? '' : 's'}
          </span>
          <div className="acp-plan-side">
            <span>Speed</span>
            <button type="button" className="acp-btn" aria-label="Slower" onClick={() => setBaseSpeed(plan.route.baseSpeedKn - 0.5)}>−</button>
            <span style={{ minWidth: 46, textAlign: 'center' }}>{plan.route.baseSpeedKn.toFixed(1)} kn</span>
            <button type="button" className="acp-btn" aria-label="Faster" onClick={() => setBaseSpeed(plan.route.baseSpeedKn + 0.5)}>+</button>
          </div>
        </div>
      )}
    </div>
  );
}
